import type { FieldPolicy, FieldFunctionOptions } from '@apollo/client';
import {
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  defaultDataIdFromObject,
  ApolloLink,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { isNonNil, now, ZodUrl } from '@shady-pines/types';
import type { SupabaseClient } from '@supabase/supabase-js';
import { persistCache } from 'apollo3-cache-persist';
import type { PersistentStorage } from 'apollo3-cache-persist';
import { addMinutes, isAfter, parseISO } from 'date-fns';
import { isEmpty, isNil } from 'lodash';
import type { SupabaseAnonKey } from './supabase';

type Edge<T> = {
  node: T;
  cursor?: string;
};

type PageInfo = {
  hasNextPage: boolean;
  endCursor: string | null;
};

type Collection<T> = {
  edges: Edge<T>[];
  pageInfo: PageInfo;
};

const mergePaginatedResults = <T>(
  existing: Collection<T> | undefined,
  incoming: Collection<T>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options: FieldFunctionOptions<Record<string, any>, Record<string, any>>,
): Collection<T> => {
  if (!existing) {
    return incoming;
  }
  if (!options.args?.after) {
    return incoming;
  }

  const existingIds = new Set<string>();

  for (const edge of existing.edges) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if ('id' in edge.node && typeof edge.node.id === 'string') {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      existingIds.add(edge.node.id);
    }
  }

  const newEdges = incoming.edges.filter((edge) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if ('id' in edge.node && typeof edge.node.id === 'string') {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return !existingIds.has(edge.node.id);
    }
    return true;
  });

  return {
    edges: [...existing.edges, ...newEdges],
    pageInfo: incoming.pageInfo,
  };
};

const createCollectionPolicy = <T>(): FieldPolicy<
  Collection<T> | undefined,
  Collection<T> | undefined,
  Collection<T>
> => ({
  keyArgs: ['filter', 'orderBy', 'after'],
  merge: mergePaginatedResults,
});

export const CACHE_TIMESTAMP_KEY = 'apollo-cache-timestamp';

const cache = new InMemoryCache({
  dataIdFromObject(responseObject: { nodeId?: number; id?: string; __typename?: string }) {
    if ('nodeId' in responseObject && typeof responseObject.nodeId === 'number') {
      return responseObject.nodeId.toString();
    }

    if ('id' in responseObject && typeof responseObject.id === 'string') {
      const typename = responseObject.__typename;
      return typename ? `${typename}:${responseObject.id}` : responseObject.id;
    }

    return defaultDataIdFromObject(responseObject);
  },

  typePolicies: {
    Query: {
      fields: {
        showsCollection: createCollectionPolicy(),
        configurationsCollection: createCollectionPolicy(),
        shows_publicCollection: createCollectionPolicy(),
        messaging_messagesCollection: createCollectionPolicy(),
        profilesCollection: createCollectionPolicy(),
        featuresCollection: createCollectionPolicy(),
        popupsCollection: createCollectionPolicy(),
        user_subscriptionsCollection: createCollectionPolicy(),
        track_history_recent_publicCollection: createCollectionPolicy(),
        track_historyCollection: createCollectionPolicy(),
      },
    },
  },
});

// Add this before creating the client
const loggingLink = new ApolloLink((operation, forward) => {
  return forward(operation).map((result) => {
    console.debug('Cache after operation:', cache.extract());
    return result;
  });
});

export const createApolloClient = async (
  supabaseUrl: string,
  anonKey: SupabaseAnonKey,
  supabaseClient: SupabaseClient,
  storage?: PersistentStorage<string>,
  connectToDevTools = false,
) => {
  console.log('creating apollo ');
  const url = `${ZodUrl.parse(supabaseUrl)}/graphql/v1`;

  if (isNonNil(storage)) {
    const cachedAt = await storage.getItem(CACHE_TIMESTAMP_KEY);

    if (isEmpty(cachedAt) || isNil(cachedAt)) {
      await cache.reset();
      await storage.setItem(CACHE_TIMESTAMP_KEY, now());
    } else {
      const cachedAtDate = parseISO(cachedAt);
      const cacheExpiry = addMinutes(cachedAtDate, 10);
      if (isAfter(new Date(), cacheExpiry)) {
        await cache.reset();
        await storage.setItem(CACHE_TIMESTAMP_KEY, now());
      } else {
        await persistCache({ cache, storage });
      }
    }
  }

  const httpLink = createHttpLink({
    uri: url,
  });

  const authLink = setContext(async (_, prevContext) => {
    const token = (await supabaseClient.auth.getSession()).data.session?.access_token;
    return {
      headers: {
        ...prevContext.headers,
        Authorization: token ? `Bearer ${token}` : '',
        apikey: anonKey,
      },
    };
  });
  const client = new ApolloClient({
    link: loggingLink.concat(authLink.concat(httpLink)),
    connectToDevTools,
    cache,

    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy(currentFetchPolicy) {
          // After first request, use cache-first policy
          return currentFetchPolicy === 'cache-and-network' ? 'cache-first' : currentFetchPolicy;
        },
      },
      query: {
        fetchPolicy: 'cache-first',
      },
    },
  });

  return client;
};
